import React, { useState, useEffect } from "react";
import api from "../../../api/axiosConfig";
import Loader from "../../Shared/Loader";
import { showToast } from "../../Shared/Toaster";
import "./CreateRefundModal.css";

interface ITicket {
  _id: string;
  issueType: string;
  raisedBy: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  ageing: number;
  childName?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhone?: string;
  parentAuth0Id?: string;
  patientId?: string;
  deviceTokens?: string[];
  message?: string;
  paymentId?: string;
  payment_id?: string;
  appointmentId?: string;
  appointment_id?: string;
  appointmentDate?: string;
}

interface Props {
  ticket: ITicket;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateRefundModal: React.FC<Props> = ({ ticket, onClose, onSuccess }) => {
  const [paymentId, setPaymentId] = useState<string>("");
  const [paymentPercentage, setPaymentPercentage] = useState<string>("");
  const [actualRefundPercentage, setActualRefundPercentage] = useState<number>(100);
  const [reason, setReason] = useState<string>("");
  const [loadingCalc, setLoadingCalc] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    // 1. Autofill Payment ID from ticket
    const pId = (ticket as any).paymentId || (ticket as any).payment_id || (ticket as any).paymentID || "";
    setPaymentId(pId);

    // 2. Fetch appointment details & refund policy to calculate refund percentage
    calculateRefundPercentage();
  }, [ticket]);

  const calculateRefundPercentage = async () => {
    setLoadingCalc(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const apptId = ticket.appointmentId || (ticket as any).appointment_id;
      const refundInitiationDate = new Date(); // Use today's date when refund is initiated

      let bookedOnDate: Date = refundInitiationDate;
      let screeningDate: Date = (ticket as any).appointmentDate ? new Date((ticket as any).appointmentDate) : new Date();

      // Step A: Fetch Appointment details if appointmentId exists
      if (apptId) {
        try {
          const apptRes = await api.appointment_api.get(`/api/appointments/${apptId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (apptRes.data) {
            if (apptRes.data.booked_on) {
              bookedOnDate = new Date(apptRes.data.booked_on);
            }
            if (apptRes.data.date) {
              screeningDate = new Date(apptRes.data.date);
            }
          }
        } catch (err) {
          console.error("Error fetching appointment details for refund calculation:", err);
        }
      }

      // Step B: Fetch Refund Policy
      let policyRes;
      try {
        policyRes = await api.payment_api.get("/refund-policy", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch {
        policyRes = await api.payment_api.get("/pricing/refund-policy", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      }

      const policy = policyRes.data?.policy;

      if (policy) {
        // New schema fields (with fallback to old schema for backward compat)
        const n1Days: number = policy.n1_days ?? 3;
        const n2Days: number = policy.n2_days ?? 5;
        const withinN1Pct: number = policy.within_n1_days_percentage ?? policy.within_3_days_percentage ?? 100;
        const afterN1UntilN2Pct: number = policy.after_n1_until_n2_percentage ?? policy.after_3_days_until_screening_percentage ?? 75;
        const withinN2Pct: number = policy.within_n2_days_of_screening_percentage ?? 50;
        const onScreeningPct: number = policy.on_screening_date_percentage ?? 0;

        // Reset hours to start of day for strict calendar date calculations
        const bookedTime = new Date(bookedOnDate.getFullYear(), bookedOnDate.getMonth(), bookedOnDate.getDate()).getTime();
        const refundTime = new Date(refundInitiationDate.getFullYear(), refundInitiationDate.getMonth(), refundInitiationDate.getDate()).getTime();
        const screeningTime = new Date(screeningDate.getFullYear(), screeningDate.getMonth(), screeningDate.getDate()).getTime();

        // Days elapsed from booking date to refund initiation date
        const diffDaysFromBooking = Math.floor((refundTime - bookedTime) / (1000 * 60 * 60 * 24));

        // Days from refund initiation date to screening date
        const diffDaysToScreening = Math.floor((screeningTime - refundTime) / (1000 * 60 * 60 * 24));

        let calculatedPercent: number;

        if (refundTime >= screeningTime) {
          // On or after the screening date → on_screening_date_percentage
          calculatedPercent = onScreeningPct;
        } else if (diffDaysFromBooking <= n1Days) {
          // Within N1 days of payment → within_n1_days_percentage
          calculatedPercent = withinN1Pct;
        } else if (diffDaysToScreening >= n2Days) {
          // More than N2 days before screening → after_n1_until_n2_percentage
          calculatedPercent = afterN1UntilN2Pct;
        } else {
          // Within N2 days of screening date → within_n2_days_of_screening_percentage
          calculatedPercent = withinN2Pct;
        }

        setActualRefundPercentage(calculatedPercent);
        setPaymentPercentage(String(calculatedPercent));
      } else {
        setActualRefundPercentage(100);
        setPaymentPercentage("100");
      }
    } catch (err) {
      console.error("Error calculating refund percentage:", err);
      setActualRefundPercentage(100);
      setPaymentPercentage("100");
    } finally {
      setLoadingCalc(false);
    }
  };

  const handleSubmitRefund = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!paymentId.trim()) {
      showToast("Payment ID is required.", "error");
      return;
    }

    const updatedPctNum = parseFloat(paymentPercentage);
    if (isNaN(updatedPctNum) || updatedPctNum < 1 || updatedPctNum > 100) {
      showToast("Refund percentage must be between 1 and 100.", "error");
      return;
    }

    if (!reason.trim()) {
      showToast("Reason is required for refund.", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = sessionStorage.getItem("accessToken");
      const headers = token
        ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        : { "Content-Type": "application/json" };

      const parentData = {
        userId: (ticket as any).parentAuth0Id || (ticket as any).raisedBy || (ticket as any).patientId || "",
        email: (ticket as any).parentEmail || "",
        name: (ticket as any).parentName || "",
        phoneNumber: (ticket as any).parentPhone || "",
        auth0Id: (ticket as any).parentAuth0Id || (ticket as any).raisedBy || "",
        deviceTokens: (ticket as any).deviceTokens || [],
      };

      const payload = {
        razorpay_payment_id: paymentId,
        actual_refund_percentage: actualRefundPercentage,
        updated_refund_percentage: updatedPctNum,
        reason: reason,
        parent: parentData,
      };

      try {
        await api.payment_api.post("/payments/refund", payload, { headers });
      } catch {
        await api.payment_api.post("/payments/refunds/create", payload, { headers });
      }

      showToast("Refund processed successfully!", "success");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error processing refund:", err);
      const errMsg = err.response?.data?.message || "Failed to process refund.";
      showToast(errMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-refund-overlay" onClick={onClose}>
      <div className="create-refund-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="create-refund-header">
          <h2>Create Refund</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmitRefund} className="create-refund-form">
          <div className="form-group">
            <label>
              Payment ID <span className="required">*</span>
            </label>
            <div className="input-wrapper">
              <input
                type="text"
                placeholder="Enter Payment ID"
                value={paymentId}
                onChange={(e) => setPaymentId(e.target.value)}
                required
              />
              <span className="input-icon">💳</span>
            </div>
            <p className="helper-text">
              Enter the payment ID for which the refund is being processed.
            </p>
          </div>

          <div className="form-group">
            <label>
              Approved refund Percentage <span className="required">*</span>
            </label>
            <div className="input-wrapper">
              <input
                type="number"
                min="1"
                max="100"
                placeholder="Enter percentage"
                value={paymentPercentage}
                onChange={(e) => setPaymentPercentage(e.target.value)}
                required
              />
              <span className="percent-icon">%</span>
            </div>
            <p className="helper-text">
              {loadingCalc
                ? "Calculating policy refund percentage..."
                : "Enter refund percentage (1 - 100)."}
            </p>
          </div>

          <div className="form-group">
            <label>
              Reason <span className="required">*</span>
            </label>
            <textarea
              placeholder="Enter reason for refund"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            ></textarea>
            <p className="helper-text">Provide a clear reason for the refund.</p>
          </div>

          {/* Action Buttons */}
          <div className="create-refund-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader inline size="small" text="Submitting..." />
              ) : (
                <>
                  <span className="send-icon">✈</span> Submit Refund
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRefundModal;
